/*
 * Property search results page: resolves the location from the URL or keyword query,
 * geocodes it, and renders the listing, map, filters, and paging.
 * SSR and browser cards use bounded ES pages and exact live counts.
 * Full map tiles load independently when visible; request generations discard stale work.
 * Property pin focus refreshes only markers, preserving list bounds, cursors, and the selected card.
 * Highlighted cards omit the separator that floated behind their top edges.
 * Cursor generations bind navigation to completed criteria and bounds resets.
 * Declares SSR work and completes it after successful primary content and metadata commit.
 * Publishes a canonical location catalog graph for visible city cards, using MLS labels when addresses are absent.
 * Keywords that match no place fall back to the default location instead of failing the page.
 */
import { beginSSRRender } from './ssr-render';
import { app, Component } from 'apprun';
import { paginateAssets, PageSize, getPropertyLink, moveTo, $, showLoading, getPropertyIcon, hasPropertyType, hasExactPropertyType, checkMapList, checkValueInArray } from './utility';
import { getPropertyByID, getPropertiesInside_cosmos, getPropertiesPage, sortProperties, getPropertyDetailStatusPrice, getGeoCode, geocodePlace, getCommercialAgents } from './model/property';
import Porperty from './template/search-property-template';
import { lang, __ } from './Txlat';
import { getLocationKeyword, getLocationPage, PROVINCES } from './location-path';
import { applyLocationSeo, buildLocationSeo, setLocationHeading } from './location-seo';
import structuredData from '../structured-data/shared.cjs';
import { locationGraph } from './structured-data/location';
import PageControl from './pageControl';
import './osm-map.ts';
import { SearchFilter } from './searchFilter';
import InquiryForm from './inquiry-form';

const isSSR = () => (window as any).__RLP_SSR__ === true;

function getMapProperties(properties) {
  const thumbnail = property => `${property.property_photo ? `<div class="item-header"><a target=property${property.id} href="${url(property)}"><img class="img-fluid" src="${property.property_photo}" alt="${property.address}"/></a></div>` : ''}`;
  const url = property => lang.get() === 'fr' ? getPropertyLink(property, 'fr') : getPropertyLink(property, 'en');

  const popup = (property) => {
    const sp = getPropertyDetailStatusPrice(property);
    return `<div class="item-wrap">
${thumbnail(property)}
<div class="item-body flex-grow-1">
<h2 class="item-title">
  <a target=property${property.id} href=${url(property)}>${property.address}</a>
</h2>
<ul class="list-unstyled item-info">
  ${sp.price ? `<li class="item-price">${sp.price}</li>` : ''}
  ${property.property_type ? `<li class="item-type">${lang.get() === 'en' ? (property.property_type[0] ? property.property_type[0].toUpperCase() : '') : (property.property_type[1] ? property.property_type[1].toUpperCase() : '')}</li>` : ''}
</ul>
</div>
</div>`;
  };

  return properties.map(property => ({
    id: property.id,
    title: property.address,
    lat: property.latitude,
    lng: property.longitude,
    marker: getPropertyIcon(property.property_type),
    retinaMarker: getPropertyIcon(property.property_type),
    popup: popup(property)
  }));
}

function setSort(sortBy) {
  const e = $('#ajax_sort_properties');
  for (let i = 0; i < e.getElementsByTagName('option').length; i++) {
    e.getElementsByTagName('option')[i].selected = e.getElementsByTagName('option')[i].value === sortBy;
  }
}

const filterProperties = function (state) {

  let allStatusStr = '';
  const statuses = document.getElementsByName('status[]')[0] as HTMLSelectElement;

  for (let i = 0; i < statuses.selectedOptions.length; i++) {
    if (statuses.selectedOptions[i].value) {
      if (allStatusStr === '') {
        allStatusStr = `'${statuses.selectedOptions[i].value.replace('for-', '')}'`;
      } else {
        allStatusStr += `,'${statuses.selectedOptions[i].value.replace('for-', '')}'`;
      }
    }
  }

  let forLease = true;
  let forSale = true;

  if (allStatusStr) {
    forLease = allStatusStr.includes('lease');
    forSale = allStatusStr.includes('sale');
  }

  let allTypeStr = '';
  const typeOptions = document.getElementsByName('type[]')[0] as HTMLSelectElement;
  for (let i = 0; i < typeOptions.selectedOptions.length; i++) {
    if (typeOptions.selectedOptions[i].value) {
      if (allTypeStr === '') {
        allTypeStr = `'${typeOptions.selectedOptions[i].value}'`;
      } else {
        allTypeStr += `,'${typeOptions.selectedOptions[i].value}'`;
      }
    }
  }

  const allTypes = allTypeStr ? allTypeStr.split('\'').join('').split(',') : '';

  const minArea = ($('[name="min-area"]') as HTMLInputElement).value;
  const maxArea = ($('[name="max-area"]') as HTMLInputElement).value;

  const minPriceStr = ($('[name="min-price"]') as HTMLInputElement).value;
  const maxPriceStr = ($('[name="max-price"]') as HTMLInputElement).value;

  let minPrice = 0;
  if (minPriceStr && minPriceStr !== 'any') {
    minPrice = parseFloat(minPriceStr);
  }

  let maxPrice = 100000000;
  if (maxPriceStr && maxPriceStr !== 'any') {
    maxPrice = parseFloat(maxPriceStr);
  }

  if (forLease && !forSale) {
    state.properties = state.properties.filter(p => p.status.toLowerCase().includes('lease') || p.status.toLowerCase().includes('both'));
  } else if (!forLease && forSale) {
    state.properties = state.properties.filter(p => p.status.toLowerCase().includes('sale') || p.status.toLowerCase().includes('both'));
  }

  let types;
  if (allTypes) {
    types = allTypes.map((element) => {
      return element.replace('_fr', '');
    });
  }

  if (types && types.length > 0) {

    const matchTypeP = [];
    let specialMatch;
    state.properties = state.properties.filter(p => p.province.toLowerCase() !== 'qc'
      || (p.province.toLowerCase() === 'qc' && p.property_type[0] &&
        (!p.property_type[0].toLowerCase().includes('duplex')
          && !p.property_type[0].toLowerCase().includes('triplex')
          && !p.property_type[0].toLowerCase().includes('quadruplex')
        )
      ));

    specialMatch = state.properties.filter(p => p.property_type && types.find(t => t === 'multi-family')
      &&
      (hasPropertyType(p.property_type[0], 'multifamily')
        || hasPropertyType(p.property_type[0], 'condo') || hasPropertyType(p.property_type[0], 'unit')
        || hasPropertyType(p.property_type[0], 'duplex') || hasPropertyType(p.property_type[0], 'triplex')
        || hasPropertyType(p.property_type[0], 'fourplex') || hasPropertyType(p.property_type[0], 'quintuplex')
        || hasPropertyType(p.property_type[0], 'two or more storey')
      )
    );
    specialMatch.forEach((s) => {
      if (!matchTypeP.includes(s)) {
        matchTypeP.push(s);
      }
    });

    specialMatch = state.properties.filter(p => p.property_type && types.find(t => t === 'retail')
      && hasPropertyType(p.property_type[0], 'hospitality'));

    specialMatch.forEach((s) => {
      if (!matchTypeP.includes(s)) {
        matchTypeP.push(s);
      }
    });

    specialMatch = state.properties.filter(p => p.property_type && types.find(t => t === 'land')
      &&
      (
        hasExactPropertyType(p.property_type[0], 'lot') || hasExactPropertyType(p.property_type[0], 'part woods')
        || hasExactPropertyType(p.property_type[0], 'part bush') || hasExactPropertyType(p.property_type[0], 'part cleared') || hasExactPropertyType(p.property_type[0], 'part workable')
        || hasPropertyType(p.property_type[0], 'mobile/mini') || hasPropertyType(p.property_type[0], 'no building onsite')
        || hasPropertyType(p.property_type[0], 'vacant') || hasPropertyType(p.property_type[0], 'acreage')

      ));
    specialMatch.forEach((s) => {
      if (!matchTypeP.includes(s)) {
        matchTypeP.push(s);
      }
    });

    specialMatch = state.properties.filter(p => p.property_type && types.find(t => t === 'agricultural')
      &&
      (hasPropertyType(p.property_type[0], 'agriculture') || hasPropertyType(p.property_type[0], 'farm'))
    );

    specialMatch.forEach((s) => {
      if (!matchTypeP.includes(s)) {
        matchTypeP.push(s);
      }
    });

    specialMatch = state.properties.filter(p => p.property_type && types.find(t => t === 'other')
      &&
      (
        hasExactPropertyType(p.property_type[0], 'commercial') || hasExactPropertyType(p.property_type[0], 'res')
        || hasPropertyType(p.property_type[0], 'bulk')
        || hasPropertyType(p.property_type[0], 'residential') || hasPropertyType(p.property_type[0], 'detached')
        || hasPropertyType(p.property_type[0], 'parking') || hasPropertyType(p.property_type[0], 'unknown')
        || (p.property_type[0] && p.property_type[0].indexOf('year round access') >= 0)
      ));

    specialMatch.forEach((s) => {
      if (!matchTypeP.includes(s)) {
        matchTypeP.push(s);
      }
    });

    specialMatch = state.properties.filter(p => p.property_type && types.find(t => t === 'hotels/motels')
      &&
      (hasPropertyType(p.property_type[0], 'hospitality'))
    );

    specialMatch.forEach((s) => {
      if (!matchTypeP.includes(s)) {
        matchTypeP.push(s);
      }
    });

    specialMatch = state.properties.filter(p => p.property_type && types.find(t => t === 'investment')
      &&
      (hasPropertyType(p.property_type[0], 'investment') || hasPropertyType(p.property_type[0], 'institutional'))
    );

    specialMatch.forEach((s) => {
      if (!matchTypeP.includes(s)) {
        matchTypeP.push(s);
      }
    });

    types.forEach((t) => {
      state.properties.filter(p => p.property_type && hasPropertyType(p.property_type[0], t)).forEach((item) => {
        if (!matchTypeP.includes(item)) {
          matchTypeP.push(item);
        }
      });

    });
    state.properties = matchTypeP;
  }

  if (minArea) {
    state.properties = state.properties.filter(p => p.total_area >= parseInt(minArea));
  }

  if (maxArea) {
    state.properties = state.properties.filter(p => p.total_area <= parseInt(maxArea));
  }

  if (minPrice) {
    if (!forLease && !forSale || forLease && forSale) {
      state.properties = state.properties.filter((p) => {
        if (p.status === 'both') {
          return (p.lease_price >= minPrice || p.sale_price >= minPrice);
        } else if (p.status === 'sale') {
          return (p.sale_price >= minPrice);
        } else if (p.status === 'lease') {
          return (p.lease_price >= minPrice);
        } else {
          return false;
        }
      }
      );
    } else if (forLease && !forSale) {
      state.properties = state.properties.filter((p) => {
        if (p.status === 'lease') { return (p.lease_price >= minPrice); } else { return false; }
      }
      );
    } else if (!forLease && forSale) {
      state.properties = state.properties.filter((p) => {
        if (p.status === 'sale') { return (p.sale_price >= minPrice); } else { return false; }
      }
      );
    }
  }

  if (maxPrice) {
    if (!forLease && !forSale || forLease && forSale) {
      state.properties = state.properties.filter((p) => {
        if (p.status === 'both') {
          return (p.lease_price <= maxPrice || p.sale_price <= maxPrice);
        } else if (p.status === 'sale') {
          return (p.sale_price <= maxPrice);
        } else if (p.status === 'lease') {
          return (p.lease_price <= maxPrice);
        } else {
          return false;
        }
      }
      );
    } else if (forLease && !forSale) {
      state.properties = state.properties.filter((p) => {
        if (p.status === 'lease') { return (p.lease_price <= maxPrice); } else { return false; }
      }
      );
    } else if (!forLease && forSale) {
      state.properties = state.properties.filter((p) => {
        if (p.status === 'sale') { return (p.sale_price <= maxPrice); } else { return false; }
      }
      );
    }
  }

  const exclusiveListing = ($('[name="exclusiveListing"]') as HTMLInputElement).checked;

  if (exclusiveListing) {

    state.properties = state.properties.filter(p => (checkValueInArray(state.commercialAgents, 'alaia_id', p.agent_id)
      || checkValueInArray(state.commercialAgents, 'id', p.agent_person_id)
      || checkValueInArray(state.commercialAgents, 'alaia_id', p.agent2_id)
      || checkValueInArray(state.commercialAgents, 'id', p.agent2_person_id)) && (p.feedId === 2 || p.feedId === 41)
    );
  }

  state.properties = sortProperties(state.properties, state.sortby);

  state.pageProperties = paginateAssets(state.properties, 1);
  state.page = 1;

  return state;

};

export default class searchResult extends Component {
  private completeSSR = beginSSRRender();

  rendered = (state) => {
    if (state.pageProperties !== undefined) { this.completeSSR(); }
  };


  view = (state) => {

    if (state.pageProperties === undefined) { return; }
    const seo = buildLocationSeo(location.pathname, location.search, state.totalResults ?? state.properties.length);
    applyLocationSeo(document, seo);
    structuredData.writeGraph(document, locationGraph(location.pathname, seo,
      state.pageProperties.map(property => ({ name: property.address, mlsId: property.mls_id, url: getPropertyLink(property, lang.get()) }))));

    const params = new URLSearchParams(state.queryString);
    params.set('sortby', state.sortby ? state.sortby : '');
    app.render($('#resultFound'), <PageControl page={state.page} total={state.totalResults ?? state.properties.length} size={PageSize} visible={state.pageProperties.length} event='/gotoPage' type='property' isMlsDeleted={state.mlsDeleted} />);

    return (
      <div>
        <div id='houzez_ajax_container'>
          <div class='card-deck'>
            {state.highlighedProp &&
              <div style='width:100%'><Porperty property={state.highlighedProp} hightlight={true} />
              </div>}
            {state.pageProperties.map(p => <Porperty property={p} hightlight={false} />)}
          </div>

          {(state.totalResults ?? state.properties.length) && !state.mlsDeleted ? <PageControl page={state.page} total={state.totalResults ?? state.properties.length} size={PageSize} visible={state.pageProperties.length} event='/gotoPage' type='property' isMlsDeleted={state.mlsDeleted} /> : <></>}
          <p></p>
        </div >

      </div>
    );
  };

  update = {
    '#': async (state) => {
      this.resolvingLocation = true;
      const request = ++this.listRequest;
      ++this.markerRequest;
      this.mapProperties = [];
      try {

        let keyword = getLocationKeyword(window.location.pathname);
        setLocationHeading(document, '#properties-location-title', window.location.pathname);

        checkMapList(true);
        this.wasMapVisible = this.mapVisible();
        const params = new URLSearchParams(location.search);
        const keywordParam = params.get('keyword')?.trim().replace('/', '') || '';
        if (keywordParam) {
          keyword = keywordParam;
        }

        const sortBy = params.get('sortby') ? params.get('sortby') : '';

        setSort(sortBy);
        state.highlighedProp = null;

        if (keyword) {
          keyword = keyword.replace(/\s+/g, '');
          keyword = keyword.replace(/-/g, ' ');
        }
        const locationPage = getLocationPage(window.location.pathname);
        const locationSearch = !keywordParam && locationPage?.kind === 'properties'
          && PROVINCES.includes(locationPage.province);
        if (keyword && !locationSearch) {
          const mlsMatch = await getPropertyByID(keyword);
          if (request !== this.listRequest) { return; }

          //gcode is array means cosmos results found, if not returns google geocode
          const quebecmulti = (mlsMatch && mlsMatch.province.toLowerCase() === 'qc' && mlsMatch.property_type[0] &&
            (mlsMatch.property_type[0].toLowerCase().includes('duplex')
              || mlsMatch.property_type[0].toLowerCase().includes('triplex')
              || mlsMatch.property_type[0].toLowerCase().includes('quadruplex'))
          ) ?? false;

          if (mlsMatch && quebecmulti === false) {
            mlsMatch.distance = 0;
            const properties = [mlsMatch];
            if (request !== this.listRequest) { return; }
            this.resolvingLocation = false;
            if (this.mapVisible()) { app.run('map', getMapProperties(properties), false); }
            return { ...state, totalResults: 1,
              all_properties: properties,
              properties,
              unfilteredProperties: properties,
              pageProperties: properties,
              page: 1,
              sortby: '',
              mlsMatch: true
            };
          }
        }

        state.mlsMatch = false;
        state.mlsDeleted = false;

        const gcode = await getGeoCode(keyword);
        if (request !== this.listRequest) { return; }
        const bounds = geocodePlace(gcode).geometry.viewport;
        this.resolvingLocation = false;
        const result = await this.loadPage({ ...state, geoBounds: bounds, sortby: sortBy },
          bounds.southwest, bounds.northeast, 1, null, request);
        if (result && this.mapVisible()) { app.run('/map-set-boundary', bounds); }
        return result;
      } catch (error) {
        if (request !== this.listRequest) { return; }
        throw error;
      } finally {
        if (request === this.listRequest) { this.resolvingLocation = false; }
      }
    },

    '/highlight-card': async (state, map_property) => {
      moveTo('.card:first-of-type');
      let highlighedProp = '';
      if (map_property) {
        highlighedProp = (this.mapProperties.length ? this.mapProperties : state.all_properties).find(p => p.id === map_property.id);
      }
      return { ...state, highlighedProp };
    },

    '/sort': async (state) => {
      const sortby = ($('#ajax_sort_properties') as HTMLInputElement).value;
      const url = new URL(window.location.href);
      url.searchParams.set('sortby', sortby);
      window.history.pushState({}, '', url.href);
      return this.reloadCriteria({ ...state, sortby });
    },

    '/filter-list': async (state) => {
      this.pushHistory();
      return this.reloadCriteria(state);
    },

    '/show-property-map': (state, property) => {
      this.pendingMarker = property.address;
      return this.openMap(state, property);
    },

    '/set_map_view': (state, value) => {
      if (String(value) === '1') { return this.openMap(state); }
      const url = new URL(window.location.href);
      url.searchParams.set('map_view', '2');
      window.history.pushState({}, '', url.href);
      checkMapList(true);
      this.wasMapVisible = this.mapVisible();
      ++this.markerRequest;
      this.pendingMarker = null;
      return state;
    },

    '/viewport-resize': (state) => {
      checkMapList(true);
      const visible = this.mapVisible();
      const opening = visible && !this.wasMapVisible;
      this.wasMapVisible = visible;
      if (!visible) { ++this.markerRequest; this.pendingMarker = null; }
      else if (opening && !this.resolvingLocation) {
        if (state.mlsMatch) { app.run('map', getMapProperties(state.properties), false); }
        else if (state.geoBounds) { app.run('/map-set-boundary', state.geoBounds, true); }
      }
      return state;
    },

    '/map-load-list': async (state, lowerBounds, upperBounds) => {
      if (this.resolvingLocation || !this.mapVisible() || state.mlsMatch || state.mlsDeleted) { return; }
      app.run('/load-map-markers', lowerBounds, upperBounds);
      return this.loadPage({ ...state, geoBounds: { southwest: lowerBounds, northeast: upperBounds } }, lowerBounds, upperBounds);
    },

    '/load-map-markers': async (_state, lowerBounds, upperBounds) => {
      const request = ++this.markerRequest;
      if (!this.mapVisible()) { return; }
      if (upperBounds.lat - lowerBounds.lat > 5 || upperBounds.lng - lowerBounds.lng > 5) {
        this.mapProperties = [];
        app.run('map', [], true);
        const hint = $('#map-search-message');
        if (hint) { hint.textContent = __('Too big geographical area. Please refine the search.'); }
        return;
      }
      const hint = $('#map-search-message');
      if (hint) { hint.textContent = lang.get() === 'fr' ? 'Chargement des propriétés sur la carte…' : 'Loading map listings…'; }
      const exclusive = !!new URLSearchParams(location.search).get('exclusive-listing');
      let properties, agents;
      try {
        [properties, agents] = await Promise.all([
          getPropertiesInside_cosmos({ lowerBounds, upperBounds }), exclusive ? getCommercialAgents() : Promise.resolve([])
        ]);
      } catch (error) {
        if (request === this.markerRequest && this.mapVisible() && hint) {
          hint.textContent = lang.get() === 'fr' ? 'Impossible de charger les propriétés sur la carte. Réessayez.'
            : 'Unable to load map listings. Please try again.';
        }
        return;
      }
      if (request !== this.markerRequest || !this.mapVisible()) { return; }
      if (hint) { hint.textContent = ''; }
      this.mapProperties = properties;
      this.mapAgents = agents;
      app.run('/render-map-markers', request);
      // Marker completion never returns a captured card state.
    },

    '/render-map-markers': (state, request) => {
      if (request !== this.markerRequest || !this.mapVisible()) { return; }
      const properties = this.mapProperties.filter(p => p.province.toLowerCase() !== 'qc'
        || !p.property_type[0]?.toLowerCase().match(/duplex|triplex|quadruplex/));
      const filtered = filterProperties({ ...state, properties, commercialAgents: this.mapAgents });
      app.run('map', getMapProperties(filtered.properties), true);
      if (this.pendingMarker) {
        const title = this.pendingMarker; this.pendingMarker = null;
        app.run('/openMarker', title);
      }
    },

    '/gotoPage': async (state, page) => {
      if (this.resolvingLocation || state.cursorGeneration !== this.cursorGeneration
        || !Number.isInteger(page) || page < 1 || page > Math.ceil(state.totalResults / PageSize)
        || !(page in state.cursors)) { return; }
      return this.loadPage(state, state.geoBounds.southwest, state.geoBounds.northeast,
        page, state.cursors[page], undefined, state.cursorGeneration);
    },

    '/setFilterComponentState': async (state, { minPriceStr, maxPriceStr, forLease, forSale, allTypes, keyword, minArea, maxArea, sortBy, page, minPrice, maxPrice, exclusivelisting }) => {
      state = { ...state, minPriceStr, maxPriceStr, forLease, forSale, allTypes, keyword, minArea, maxArea, sortBy, page, minPrice, maxPrice, exclusivelisting };
      return state;
    },

    'close-highlighed-card': (state) => {
      document.querySelector('form').scrollIntoView();
      state.highlighedProp = null;
      return state;
    }
  };

  private wasMapVisible = false;
  private resolvingLocation = false;
  private pendingMarker = null;
  private listRequest = 0;
  private cursorGeneration = 0;
  private markerRequest = 0;
  private mapProperties = [];
  private mapAgents = [];

  private openMap(state, property = null) {
    const url = new URL(window.location.href);
    url.searchParams.set('map_view', '1');
    window.history.pushState({}, '', url.href);
    checkMapList(true);
    this.wasMapVisible = this.mapVisible();
    ++this.markerRequest;
    if (this.resolvingLocation || isSSR()) { return state; }
    if (state.mlsMatch) {
      app.run('map', getMapProperties(state.properties), false);
      if (this.pendingMarker) { app.run('/openMarker', this.pendingMarker); this.pendingMarker = null; }
      return state;
    }
    let bounds = state.geoBounds;
    if (property && bounds && (bounds.northeast.lat - bounds.southwest.lat > 5 || bounds.northeast.lng - bounds.southwest.lng > 5)) {
      bounds = { southwest: {lat: Math.max(-90, property.latitude - 0.01), lng: Math.max(-180, property.longitude - 0.01)},
        northeast: {lat: Math.min(90, property.latitude + 0.01), lng: Math.min(180, property.longitude + 0.01)} };
    }
    if (bounds) {
      if (property) { app.run('/map-set-boundary', bounds, true); }
      else { app.run('/map-set-boundary', bounds); }
    }
    return property ? state : { ...state, geoBounds: bounds };
  }

  private mapVisible() {
    return !isSSR() && (window.innerWidth >= 768 || new URLSearchParams(location.search).get('map_view') === '1');
  }

  private async reloadCriteria(state) {
    if (this.resolvingLocation) { return; }
    if (state.mlsMatch || !state.geoBounds) { return state; }
    showLoading('#resultFound');
    if (this.mapVisible()) {
      app.run('/load-map-markers', state.geoBounds.southwest, state.geoBounds.northeast);
    } else { ++this.markerRequest; }
    return this.loadPage(state, state.geoBounds.southwest, state.geoBounds.northeast);
  }

  private async loadPage(state, lowerBounds, upperBounds, page = 1, after = null,
    request = ++this.listRequest, cursorGeneration = ++this.cursorGeneration) {
    const queryString = location.search;
    let result;
    try {
      result = await getPropertiesPage({ lowerBounds, upperBounds, queryString, ...(after ? { after } : {}) });
    } catch (error) {
      if (request !== this.listRequest) { return; }
      throw error;
    }
    if (request !== this.listRequest) { return; }
    const offset = (page - 1) * PageSize;
    if (page > 1 && (!result.properties.length || offset + result.properties.length > result.total)) {
      return this.loadPage(state, lowerBounds, upperBounds);
    }
    const cursors = page === 1 ? { 1: null } : { ...state.cursors };
    if (result.after && offset + result.properties.length < result.total) { cursors[page + 1] = result.after; }
    return { ...state, queryString, sortby: new URLSearchParams(queryString).get('sortby') || '',
      geoBounds: { southwest: lowerBounds, northeast: upperBounds }, cursors, cursorGeneration, page,
      all_properties: result.properties, properties: result.properties, unfilteredProperties: result.properties,
      pageProperties: result.properties, totalResults: result.total, highlighedProp: null };
  }

  private pushHistory() {
    const url = new URL(window.location.href);

    const params = new URLSearchParams(location.search);

    const urlminarea = params.get('min-area');
    const urlmaxarea = params.get('max-area');
    const urlminprice = params.get('min-price');
    const urlmaxprice = params.get('max-price');
    const urlexclusivelisting = params.get('exclusive-listing');

    url.searchParams.delete('type[]');

    url.searchParams.delete('status[]');

    let allTypeStr = '';
    const types = document.getElementsByName('type[]')[0] as HTMLSelectElement;
    for (let i = 0; i < types.selectedOptions.length; i++) {
      if (types.selectedOptions[i].value) {
        if (allTypeStr === '') {
          allTypeStr = `'${types.selectedOptions[i].value}'`;
        } else {
          allTypeStr += `,'${types.selectedOptions[i].value}'`;
        }
      }
    }

    const statuses = document.getElementsByName('status[]')[0] as HTMLSelectElement;

    for (let i = 0; i < statuses.selectedOptions.length; i++) {
      if (statuses.selectedOptions[i].value) {
        url.searchParams.append('status[]', statuses.selectedOptions[i].value.toLowerCase());
      }
    }

    for (let i = 0; i < types.selectedOptions.length; i++) {
      if (types.selectedOptions[i].value) {
        url.searchParams.append('type[]', types.selectedOptions[i].value.toLowerCase());
      }
    }

    const minArea = ($('[name="min-area"]') as HTMLInputElement).value;
    const maxArea = ($('[name="max-area"]') as HTMLInputElement).value;

    const minPrice = ($('[name="min-price"]') as HTMLInputElement).value;
    const maxPrice = ($('[name="max-price"]') as HTMLInputElement).value;

    if (urlminarea || urlminarea === '') {
      url.searchParams.set('min-area', minArea);
    } else {
      url.searchParams.append('min-area', minArea);
    }

    if (urlmaxarea || urlmaxarea === '') {
      url.searchParams.set('max-area', maxArea);
    } else {
      url.searchParams.append('max-area', maxArea);
    }

    if (urlminprice || urlminprice === '') {
      url.searchParams.set('min-price', minPrice);
    } else {
      url.searchParams.append('min-price', minPrice);
    }

    if (urlmaxprice || urlmaxprice === '') {
      url.searchParams.set('max-price', maxPrice);
    } else {
      url.searchParams.append('max-price', maxPrice);
    }

    const exclusivelisting = ($('[name="exclusiveListing"]') as HTMLInputElement).checked;

    if (urlexclusivelisting || urlexclusivelisting === '') {
      url.searchParams.set('exclusive-listing', exclusivelisting ? '1' : '');
    } else {
      url.searchParams.append('exclusive-listing', exclusivelisting ? '1' : '');
    }

    window.history.pushState('', '', url.href);
  }
}

new SearchFilter().mount('.advanced-search.advanced-search-half-map');
new searchResult().start('.listing-view');
new InquiryForm({ isPopup: true }).start('.mobile-property-form .modal-body');

window.addEventListener('resize', () => app.run('/viewport-resize'));
